import * as React from "react"
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons"

import { cn } from "../../lib/utils"
import { ButtonWidget } from "./button-widget"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

const CarouselWidget = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === "ArrowRight") {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext]
    )

    React.useEffect(() => {
      if (!api) {
        return
      }

      setApi?.(api)
      onSelect(api)
      api.on("select", onSelect)
      api.on("reInit", onSelect)

      return () => {
        api.off("select", onSelect)
        api.off("reInit", onSelect)
      }
    }, [api, onSelect, setApi])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("dsw-relative", className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)
CarouselWidget.displayName = "CarouselWidget"

const CarouselWidgetContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className="dsw-overflow-hidden">
      <div
        ref={ref}
        className={cn(
          "dsw-flex",
          orientation === "horizontal" ? "dsw--ml-4" : "dsw--mt-4 dsw-flex-col",
          className
        )}
        {...props}
      />
    </div>
  )
})
CarouselWidgetContent.displayName = "CarouselWidgetContent"

const CarouselWidgetItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        "dsw-min-w-0 dsw-shrink-0 dsw-grow-0 dsw-basis-full",
        orientation === "horizontal" ? "dsw-pl-4" : "dsw-pt-4",
        className
      )}
      {...props}
    />
  )
})
CarouselWidgetItem.displayName = "CarouselWidgetItem"

const CarouselWidgetPrevious = React.forwardRef<
  React.ElementRef<typeof ButtonWidget>,
  React.ComponentPropsWithoutRef<typeof ButtonWidget>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  return (
    <ButtonWidget
      ref={ref}
      variant={variant}
      size={size}
      leftIcon={<ChevronLeftIcon className="dsw-h-4 dsw-w-4" />}
      className={cn(
        "dsw-absolute dsw-h-8 dsw-w-8 dsw-rounded-full",
        orientation === "horizontal"
          ? "dsw--left-4 dsw-top-1/2 dsw--translate-y-1/2"
          : "dsw--top-4 dsw-left-1/2 dsw--translate-x-1/2 dsw-rotate-90",
        !canScrollPrev ? "dsw-opacity-50 dsw-cursor-not-allowed" : "",
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
    </ButtonWidget>
  )
})
CarouselWidgetPrevious.displayName = "CarouselWidgetPrevious"

const CarouselWidgetNext = React.forwardRef<
  React.ElementRef<typeof ButtonWidget>,
  React.ComponentPropsWithoutRef<typeof ButtonWidget>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  return (
    <ButtonWidget
      ref={ref}
      variant={variant}
      size={size}
      leftIcon={<ChevronRightIcon className="dsw-h-4 dsw-w-4" />}  
      className={cn(
        "dsw-absolute dsw-h-8 dsw-w-8 dsw-rounded-full",
        orientation === "horizontal"
          ? "dsw--right-4 dsw-top-1/2 dsw--translate-y-1/2"
          : "dsw--bottom-4 dsw-left-1/2 dsw--translate-x-1/2 dsw-rotate-90",
        !canScrollNext ? "dsw-opacity-50 dsw-cursor-not-allowed" : "",
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
    </ButtonWidget>
  )
})
CarouselWidgetNext.displayName = "CarouselWidgetNext"

export {
  type CarouselApi,
  CarouselWidget,
  CarouselWidgetContent,
  CarouselWidgetItem,
  CarouselWidgetPrevious,
  CarouselWidgetNext,
} 